import * as THREE from "three";
import { RectAreaLightUniformsLib } from "three/addons/lights/RectAreaLightUniformsLib.js";

import { HENDERSON_SOURCE_LIGHTS } from "./lighting-fixtures";

export type HendersonLightingMode =
  | "baseline"
  | "fixture-prototype"
  | "fixture-balanced"
  | "fixture-contact";

type BlenderPoint = readonly [x: number, y: number, z: number];
type RectLightDefinition = {
  name: string;
  position: BlenderPoint;
  width: number;
  height: number;
  powerLumens: number;
  color: readonly [r: number, g: number, b: number];
  quaternion?: readonly [number, number, number, number];
  target?: BlenderPoint;
};

export type HendersonLightingRig = {
  mode: HendersonLightingMode;
  sceneObjects: THREE.Object3D[];
  rectAreaLights: THREE.RectAreaLight[];
  toneMappingExposure: number;
  environmentIntensity: number;
  dispose: () => void;
};

export const HENDERSON_FIXTURE_QUERY_VALUE = "fixtures";
export const HENDERSON_BALANCED_QUERY_VALUE = "balanced";
export const HENDERSON_CONTACT_QUERY_VALUE = "contact";
// Empirical browser gain, not a conversion of Blender watts to lumens.
const PROTOTYPE_LUMENS_PER_BLENDER_ENERGY = 0.3;
const sourceDefinitions: readonly RectLightDefinition[] = HENDERSON_SOURCE_LIGHTS.map(
  ({ energy, ...source }) => ({ ...source, powerLumens: energy * PROTOTYPE_LUMENS_PER_BLENDER_ENERGY })
);
export const HENDERSON_BLENDER_FIXTURE_LIGHTS = sourceDefinitions.filter(
  (light) => light.name.startsWith("Downlight")
);
const HENDERSON_BLENDER_SUPPORT_LIGHTS = sourceDefinitions.filter(
  (light) => !light.name.startsWith("Downlight")
);

let rectAreaUniformsReady = false;

export function hendersonLightingModeFromSearch(
  search: string
): HendersonLightingMode {
  const query = search.startsWith("?") ? search : `?${search}`;
  const value = new URLSearchParams(query).get("lighting");
  if (value === HENDERSON_CONTACT_QUERY_VALUE) return "fixture-contact";
  if (value === HENDERSON_BALANCED_QUERY_VALUE) return "fixture-balanced";
  return value === HENDERSON_FIXTURE_QUERY_VALUE
    ? "fixture-prototype"
    : "baseline";
}

function blenderToThree([x, y, z]: BlenderPoint) {
  return new THREE.Vector3(x, z, -y);
}

function ensureRectAreaLightUniforms() {
  if (rectAreaUniformsReady) return;
  RectAreaLightUniformsLib.init();
  rectAreaUniformsReady = true;
}

function createRectAreaLight(definition: RectLightDefinition) {
  const light = new THREE.RectAreaLight(
    new THREE.Color(...definition.color),
    1,
    definition.width,
    definition.height
  );
  light.name = definition.name;
  light.position.copy(blenderToThree(definition.position));
  light.power = definition.powerLumens;
  if (definition.target) {
    light.lookAt(blenderToThree(definition.target));
  } else if (definition.quaternion) {
    light.quaternion.fromArray(definition.quaternion);
  } else {
    light.rotation.x = -Math.PI / 2;
  }
  return light;
}

function configureShadowLight(light: THREE.DirectionalLight, extent = 14) {
  light.castShadow = true;
  light.shadow.mapSize.set(2048, 2048);
  light.shadow.camera.near = 0.5;
  light.shadow.camera.far = 40;
  light.shadow.bias = -0.0001;
  light.shadow.normalBias = 0.015;
  light.shadow.camera.left = -extent;
  light.shadow.camera.right = extent;
  light.shadow.camera.top = extent;
  light.shadow.camera.bottom = -extent;
  return light;
}

function ownedRig(
  scene: THREE.Scene,
  mode: HendersonLightingMode,
  sceneObjects: THREE.Object3D[],
  rectAreaLights: THREE.RectAreaLight[],
  toneMappingExposure: number,
  environmentIntensity: number
): HendersonLightingRig {
  scene.add(...sceneObjects);
  let disposed = false;
  return {
    mode,
    sceneObjects,
    rectAreaLights,
    toneMappingExposure,
    environmentIntensity,
    dispose: () => {
      if (disposed) return;
      disposed = true;
      for (const object of sceneObjects) {
        scene.remove(object);
        if (object instanceof THREE.DirectionalLight) {
          object.shadow.map?.dispose();
        }
      }
    },
  };
}

function createBaselineLightingRig(scene: THREE.Scene) {
  const hemi = new THREE.HemisphereLight(0xffffff, 0x9ca49a, 1.85);
  const key = configureShadowLight(new THREE.DirectionalLight(0xffffff, 2.55));
  key.position.set(5, 12, 6);
  const fill = new THREE.DirectionalLight(0xe8efff, 0.85);
  fill.position.set(-8, 7, -10);
  return ownedRig(scene, "baseline", [hemi, key, fill], [], 1, 0.35);
}
function balancedPowerScale(name: string) {
  if (name.startsWith("Downlight")) return 0.72;
  if (name === "FULL dry lab fill") return 0.55;
  if (name.startsWith("VIS dry linear")) return 0.7;
  if (name === "FULL entry fill") return 0.58;
  if (name.startsWith("VIS corridor linear")) return 0.72;
  if (name === "Overhead fill") return 0.65;
  if (name === "Window daylight") return 0.75;
  return 0.75;
}

const BALANCED_SAFETY_FILL: RectLightDefinition = {
  name: "Henderson safety window fill",
  position: [6.65, 11.32, 2.35],
  width: 3.05,
  height: 2.0,
  powerLumens: 70,
  color: [0.84, 0.91, 1],
  target: [7.55, 10.05, 1.25],
};

function createBalancedFixtureRig(scene: THREE.Scene) {
  ensureRectAreaLightUniforms();
  const balancedDefinitions = sourceDefinitions.map((definition) => ({
    ...definition,
    powerLumens: definition.powerLumens * balancedPowerScale(definition.name),
  }));
  const rectAreaLights = [...balancedDefinitions, BALANCED_SAFETY_FILL].map(
    createRectAreaLight
  );
  const hemi = new THREE.HemisphereLight(0xf7f9ff, 0x69716a, 0.32);
  const shadowKey = configureShadowLight(
    new THREE.DirectionalLight(0xffffff, 0.24),
    13
  );
  shadowKey.name = "Henderson balanced shadow proxy";
  shadowKey.position.set(2.25, 12, -5.7);
  shadowKey.target.name = "Henderson balanced shadow target";
  shadowKey.target.position.set(2.25, 0, -5.7);
  return ownedRig(
    scene,
    "fixture-balanced",
    [...rectAreaLights, hemi, shadowKey, shadowKey.target],
    rectAreaLights,
    1,
    0.22
  );
}

function contactPowerScale(name: string) {
  return balancedPowerScale(name) * 0.05;
}

const CONTACT_SAFETY_FILL: RectLightDefinition = {
  ...BALANCED_SAFETY_FILL,
  name: "Henderson contact safety window fill",
  powerLumens: 18,
};

function createContactFixtureRig(scene: THREE.Scene) {
  ensureRectAreaLightUniforms();
  const contactDefinitions = sourceDefinitions.map((definition) => ({
    ...definition,
    powerLumens: definition.powerLumens * contactPowerScale(definition.name),
  }));
  const rectAreaLights = [...contactDefinitions, CONTACT_SAFETY_FILL].map(
    createRectAreaLight
  );
  const hemi = new THREE.HemisphereLight(0xf7f9ff, 0x69716a, 1.3);
  const contactKey = configureShadowLight(
    new THREE.DirectionalLight(0xffffff, 1.0),
    11
  );
  contactKey.name = "Henderson overhead contact proxy";
  contactKey.position.set(4.1, 12, -5.1);
  contactKey.target.name = "Henderson overhead contact target";
  contactKey.target.position.set(4.1, 0, -5.1);
  contactKey.shadow.mapSize.set(4096, 4096);
  contactKey.shadow.intensity = 0.62;
  contactKey.shadow.radius = 3.5;
  return ownedRig(
    scene,
    "fixture-contact",
    [...rectAreaLights, hemi, contactKey, contactKey.target],
    rectAreaLights,
    1,
    0.35
  );
}

function createFixturePrototypeRig(scene: THREE.Scene) {
  ensureRectAreaLightUniforms();
  const rectAreaLights = [
    ...HENDERSON_BLENDER_FIXTURE_LIGHTS,
    ...HENDERSON_BLENDER_SUPPORT_LIGHTS,
  ].map(createRectAreaLight);
  const hemi = new THREE.HemisphereLight(0xf7f9ff, 0x69716a, 0.32);
  const shadowKey = configureShadowLight(
    new THREE.DirectionalLight(0xffffff, 0.24),
    13
  );
  shadowKey.name = "Henderson fixture shadow proxy";
  shadowKey.position.set(2.25, 12, -5.7);
  shadowKey.target.name = "Henderson fixture shadow target";
  shadowKey.target.position.set(2.25, 0, -5.7);
  return ownedRig(
    scene,
    "fixture-prototype",
    [...rectAreaLights, hemi, shadowKey, shadowKey.target],
    rectAreaLights,
    1,
    0.22
  );
}

export function createHendersonLightingRig(
  scene: THREE.Scene,
  mode: HendersonLightingMode
) {
  if (mode === "fixture-contact") return createContactFixtureRig(scene);
  if (mode === "fixture-balanced") return createBalancedFixtureRig(scene);
  return mode === "fixture-prototype"
    ? createFixturePrototypeRig(scene)
    : createBaselineLightingRig(scene);
}
